import {
    createContext,
    useContext,
    useEffect,
    useReducer,
    useState,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
} from 'react';
import type {
    AppSettingsType,
    BusinessModeFieldType,
    BusinessModeType,
    ModeFormType,
} from '@econtractor/types';
import * as apiSettings from 'api/apiSettings';
import { SetSettingsReducerd, type SetSettingsAction } from './reducer/SetSettingsReducer';
import { useToast } from './ToastContext';
import { useTranslation } from 'react-i18next';
import { useGlobal } from './GlobalContext';
import { useNavigate } from 'react-router-dom';
import { setSettingsParamsCache } from 'utils/helpers/settings/settingsParamsCache';
import { isCurrencyOnce } from 'utils/helpers/currency/currencyHelpers';

export type SettingsContextValue = {
    settings: AppSettingsType | null;
    setSettings: Dispatch<SetStateAction<AppSettingsType | null>>;
    saveSettings: () => Promise<void>;
    localSettings: AppSettingsType | null;
    dispatchLocalSettings: Dispatch<SetSettingsAction>;
    getLocaleFilterId: () => number;
    getSettings: () => Promise<void>;
};

const SettingsContext = createContext<SettingsContextValue | null>(null);

export const useSettings = (): SettingsContextValue =>
    useContext(SettingsContext) as SettingsContextValue;

/** Для UI поза SettingsProvider (тіло ModalProvider). */
export const useOptionalSettings = (): SettingsContextValue | null =>
    useContext(SettingsContext);

type SettingsContextProviderProps = {
    children: ReactNode;
};

export const SettingsContextProvider = ({ children }: SettingsContextProviderProps) => {
    const { showToast } = useToast();
    const { t } = useTranslation(['Settings', 'common']);
    const { g } = useGlobal();
    const navigate = useNavigate();

    /**
     * settings - реальні налаштування
     * localSettings - використовуються для стану форми на сторінці налаштування
     */
    const [settings, setSettings] = useState<AppSettingsType | null>(null);
    const [localSettings, dispatchLocalSettings] = useReducer(SetSettingsReducerd, null);
    const [isReady, setIsReady] = useState(false);

    const getModeFieldsFromDirs = (
        settingsData: AppSettingsType | null | undefined,
        mode: BusinessModeType,
    ): BusinessModeFieldType[] => {
        const forms = settingsData?.DIRS?.ModeForms || [];
        const form = forms.find((item: ModeFormType) => item.mode === mode)?.fields;
        return Array.isArray(form) ? form : [];
    };

    // Отримуємо параметри з сервера
    const getSettings = async () => {
        const resSetting = (await apiSettings.getSettings()) as AppSettingsType | null;
        if (!resSetting) return navigate('/login');

        const modeForms: ModeFormType[] =
            Array.isArray(resSetting?.DIRS?.ModeForms) && resSetting.DIRS.ModeForms.length
                ? resSetting.DIRS.ModeForms
                : ((await apiSettings.getModeForms()) as ModeFormType[]);

        const patchedSetting: AppSettingsType = {
            ...resSetting,
            DIRS: {
                ...(resSetting?.DIRS || {}),
                ModeForms: modeForms,
            },
        };

        const mode: BusinessModeType = resSetting?.mode || 'general';
        const customModeFields: BusinessModeFieldType[] = Array.isArray(
            patchedSetting?.customModeFields,
        )
            ? patchedSetting.customModeFields
            : [];
        const modeFieldsFromResponse: BusinessModeFieldType[] = Array.isArray(
            patchedSetting?.modeFields,
        )
            ? patchedSetting.modeFields
            : [];
        const resolvedModeFields: BusinessModeFieldType[] =
            mode === 'general'
                ? []
                : mode === 'custom'
                  ? customModeFields
                  : modeFieldsFromResponse.length
                    ? modeFieldsFromResponse
                    : getModeFieldsFromDirs(patchedSetting, mode);

        let preparedSettings: AppSettingsType = {
            ...patchedSetting,
            mode,
            customModeFields,
            modeFields: resolvedModeFields,
        };

        if (isCurrencyOnce()) {
            preparedSettings = {
                ...preparedSettings,
                RATE: { current: 1, dynamics: 0, history: [] },
            };
        }

        setSettings(preparedSettings);
        setSettingsParamsCache(preparedSettings);
        g?.setSettingsMode?.(mode);
        dispatchLocalSettings({ type: 'SET', settings: preparedSettings });
        setIsReady(true);
    };

    // Зберігаємо параметри localSettings
    const saveSettings = async () => {
        const resSetting = await apiSettings.setSettings(localSettings);
        resSetting
            ? showToast(t('messSaved'), 'success')
            : showToast(t('common:messError'), 'danger');

        if (resSetting && localSettings) {
            setSettings(localSettings);
            setSettingsParamsCache(localSettings);
            g?.setSettingsMode?.(localSettings?.mode || 'general');
        }
    };

    // Повертає ID обраної локалізації
    const getLocaleFilterId = (): number => {
        const aLocales = localSettings?.site?.locales?.split(',');
        const langDef = localSettings?.DIRS?.langs?.find(
            (lang) => lang.langPrefix === aLocales?.[0],
        );
        const lang = localSettings?.DIRS?.langs?.find(
            (lang) => lang.langPrefix === g?.localeFilter,
        );
        return lang?.ID || langDef?.ID || 1;
    };

    // ХУК - відстеження неіснуючих параметрів
    useEffect(() => {
        // Якщо параметрів немає - отримуємо їх
        if (!settings) getSettings();
        else setSettingsParamsCache(settings);
    }, [settings]);

    if (!isReady) {
        return <span className="badge text-bg-primary m-2">Get Settings...</span>;
    }

    const value: SettingsContextValue = {
        settings,
        setSettings,
        saveSettings,
        localSettings,
        dispatchLocalSettings,
        getLocaleFilterId,
        getSettings,
    };

    return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};
